/**
 * Accueil prof : ce qu'un prof doit voir en ouvrant l'app — son agenda du jour
 * et du lendemain, ce qui attend une action (copies, messages, demandes,
 * paiements) et les élèves à relancer. Tout est dérivé de requêtes déjà
 * existantes (aucune RPC dédiée) ; la présentation est PURE et testée
 * (`dashboard-overview.test.ts`).
 */

import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { RendezVousKind } from '@jeraisonne/db';
import { isRendezVousUpcoming, isVisioRoomJoinable, toEndedAtMs } from '@jeraisonne/db/visio';
import { supabase } from '@/lib/supabase';
import type { TutorBookingSlotWithReservations } from '@/lib/tutor/booking';
import type { TutorLinkedStudent } from '@/lib/tutor/queries';

const db = supabase as unknown as SupabaseClient;
const DAY_MS = 86_400_000;

/** Seuil (jours sans activité) à partir duquel un élève est « à relancer ». */
export const INACTIVE_DAYS = 7;

// ─── Dates (heure de Paris, comme tout le module rendez-vous) ─────────────────

const PARIS_DAY = new Intl.DateTimeFormat('fr-CA', {
  timeZone: 'Europe/Paris',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Jour calendaire « YYYY-MM-DD » à Paris. */
export function parisDayKey(ms: number): string {
  return PARIS_DAY.format(new Date(ms));
}

/** « Aujourd'hui » / « Demain » / « lundi 12 octobre ». */
export function dayLabel(iso: string, nowMs: number): string {
  const ms = new Date(iso).getTime();
  const key = parisDayKey(ms);
  if (key === parisDayKey(nowMs)) return "Aujourd'hui";
  if (key === parisDayKey(nowMs + DAY_MS)) return 'Demain';
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(ms));
}

/** « 17:30 » à Paris. */
export function timeLabel(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

/** Jours calendaires (Paris) depuis une date ; null = jamais. */
export function daysSince(iso: string | null, nowMs: number): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  const day = (ms: number) => Date.parse(`${parisDayKey(ms)}T00:00:00Z`);
  return Math.max(0, Math.round((day(nowMs) - day(then)) / DAY_MS));
}

// ─── Agenda ─────────────────────────────────────────────────────────────────

export type AgendaItem = {
  reservationId: string;
  studentId: string | null;
  name: string;
  startsAt: string;
  endsAt: string;
  kind: RendezVousKind;
  status: 'confirmed' | 'pending_payment';
  priceCents: number;
  joinable: boolean;
};

export type AgendaDay = { key: string; label: string; items: AgendaItem[] };

/**
 * Rendez-vous à venir (confirmés ou en attente de paiement, non archivés),
 * regroupés par jour. `horizonDays` = 2 → aujourd'hui + demain ; `later` =
 * nombre de rendez-vous plus lointains (pour « + 3 autres cette semaine »).
 */
export function buildAgenda(
  slots: readonly TutorBookingSlotWithReservations[],
  nowMs: number,
  archivedIds: ReadonlySet<string> | undefined,
  horizonDays = 2,
): { days: AgendaDay[]; later: number; next: AgendaItem | null } {
  const items: AgendaItem[] = [];
  for (const slot of slots) {
    const start = new Date(slot.starts_at).getTime();
    const end = new Date(slot.ends_at).getTime();
    for (const r of slot.reservations) {
      if (r.status !== 'confirmed' && r.status !== 'pending_payment') continue;
      if (archivedIds?.has(r.id)) continue;
      const ended = toEndedAtMs(r.ended_at);
      if (!isRendezVousUpcoming(nowMs, end, ended)) continue;
      items.push({
        reservationId: r.id,
        studentId: r.student_id,
        name: r.student_display_name ?? r.guest_name ?? r.guest_email ?? 'Élève',
        startsAt: slot.starts_at,
        endsAt: slot.ends_at,
        kind: slot.kind,
        status: r.status,
        priceCents: r.price_cents,
        joinable: r.status === 'confirmed' && isVisioRoomJoinable(nowMs, start, end, ended),
      });
    }
  }
  items.sort((a, b) => a.startsAt.localeCompare(b.startsAt));

  const allowed = new Set<string>();
  for (let i = 0; i < horizonDays; i++) allowed.add(parisDayKey(nowMs + i * DAY_MS));

  const days: AgendaDay[] = [];
  let later = 0;
  for (const item of items) {
    const key = parisDayKey(new Date(item.startsAt).getTime());
    if (!allowed.has(key)) {
      later++;
      continue;
    }
    let day = days.find((d) => d.key === key);
    if (!day) {
      day = { key, label: dayLabel(item.startsAt, nowMs), items: [] };
      days.push(day);
    }
    day.items.push(item);
  }
  return { days, later, next: items[0] ?? null };
}

// ─── Élèves à relancer ──────────────────────────────────────────────────────

export type OverdueAssignment = {
  id: string;
  student_id: string;
  due_at: string;
  exercise_title: string;
};

export type FollowUp = {
  studentId: string;
  name: string;
  /** Jours depuis la dernière activité ; null = jamais actif. */
  inactiveDays: number | null;
  inactive: boolean;
  overdue: number;
  reason: string;
};

/** Phrase courte et sans troncature : « Inactif depuis 9 j · 1 devoir en retard ». */
export function followUpReason(inactive: boolean, days: number | null, overdue: number): string {
  const parts: string[] = [];
  if (inactive) parts.push(days === null ? 'Jamais actif' : `Inactif depuis ${days} j`);
  if (overdue > 0) parts.push(`${overdue} devoir${overdue > 1 ? 's' : ''} en retard`);
  return parts.join(' · ');
}

/**
 * Élèves liés ACTIFS qui décrochent (≥ 7 j sans activité ou jamais actifs)
 * ou qui ont un devoir en retard. Les deux motifs cumulés passent en tête,
 * puis les devoirs en retard, puis la plus longue inactivité.
 */
export function studentsToFollowUp(
  students: readonly TutorLinkedStudent[],
  overdue: readonly OverdueAssignment[],
  nowMs: number,
): FollowUp[] {
  const overdueBy = new Map<string, number>();
  for (const a of overdue) overdueBy.set(a.student_id, (overdueBy.get(a.student_id) ?? 0) + 1);

  const out: FollowUp[] = [];
  for (const s of students) {
    if (s.status !== 'active') continue;
    const days = daysSince(s.last_activity_at, nowMs);
    const inactive = days === null || days >= INACTIVE_DAYS;
    const late = overdueBy.get(s.student_id) ?? 0;
    if (!inactive && late === 0) continue;
    out.push({
      studentId: s.student_id,
      name: s.display_name,
      inactiveDays: days,
      inactive,
      overdue: late,
      reason: followUpReason(inactive, days, late),
    });
  }
  const weight = (f: FollowUp) => (f.inactive ? 1 : 0) + (f.overdue > 0 ? 2 : 0);
  return out.sort(
    (a, b) =>
      weight(b) - weight(a) ||
      (b.inactiveDays ?? 10_000) - (a.inactiveDays ?? 10_000) ||
      a.name.localeCompare(b.name, 'fr'),
  );
}

// ─── À traiter + phrase-bilan ───────────────────────────────────────────────

export type TodoCounts = {
  submissions: number;
  unreadDiscussions: number;
  requests: number;
};

const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

/**
 * Phrase-bilan sous le titre : uniquement ce qui demande de l'attention, dans
 * l'ordre agenda → copies → messages. Rien à signaler → phrase rassurante.
 */
export function summarySentence(todayCount: number, todo: TodoCounts, followUps: number): string {
  const parts: string[] = [];
  if (todayCount > 0)
    parts.push(plural(todayCount, "rendez-vous aujourd'hui", "rendez-vous aujourd'hui"));
  if (todo.submissions > 0)
    parts.push(plural(todo.submissions, 'exercice reçu', 'exercices reçus'));
  if (todo.unreadDiscussions > 0)
    parts.push(plural(todo.unreadDiscussions, 'discussion non lue', 'discussions non lues'));
  if (followUps > 0) parts.push(plural(followUps, 'élève à relancer', 'élèves à relancer'));
  if (parts.length === 0)
    return 'Rien d’urgent : tes élèves avancent, profites-en pour préparer la suite.';
  return parts.join(' · ');
}

/** Premières étapes d'un prof qui démarre (la carte disparaît quand tout est fait). */
export type OnboardingStep = {
  id: 'slots' | 'students' | 'class';
  done: boolean;
};

export function onboardingSteps(input: {
  hasRules: boolean;
  hasStudents: boolean;
  hasClasses: boolean;
}): OnboardingStep[] {
  return [
    { id: 'students', done: input.hasStudents },
    { id: 'slots', done: input.hasRules },
    { id: 'class', done: input.hasClasses },
  ];
}

/** Prénom pour le bonjour (« Marc Durand » → « Marc »). */
export function firstName(displayName: string | null | undefined): string | null {
  const first = (displayName ?? '').trim().split(/\s+/)[0];
  return first ? first : null;
}

// ─── Requête : devoirs en retard (lecture sous la RLS « tuteur » existante) ───

export function useTutorOverdueAssignments(tutorId: string) {
  return useQuery({
    queryKey: ['tutor-overdue-assignments', tutorId],
    enabled: !!tutorId,
    staleTime: 60_000,
    queryFn: async (): Promise<OverdueAssignment[]> => {
      const { data, error } = await db
        .from('assignments')
        .select('id, student_id, due_at, status, exercises(title)')
        .eq('tutor_id', tutorId)
        .neq('status', 'completed')
        .lt('due_at', new Date().toISOString())
        .order('due_at', { ascending: true })
        .limit(200);
      if (error) throw new Error(error.message);
      return ((data ?? []) as Record<string, unknown>[])
        .filter((a) => typeof a.student_id === 'string' && typeof a.due_at === 'string')
        .map((a) => ({
          id: String(a.id),
          student_id: String(a.student_id),
          due_at: String(a.due_at),
          exercise_title:
            (a.exercises as { title?: string } | null)?.title ?? '(exercice supprimé)',
        }));
    },
  });
}

// ─── Semaine de mes élèves : chiffres et activité récente ─────────────────────

export type RecentAttempt = {
  id: string;
  student_id: string;
  exercise_title: string;
  /** % de réussite (0..100), null si non calculé */
  score: number | null;
  completed_at: string;
};

export type WeekStats = {
  /** élèves liés actifs */
  students: number;
  /** dont actifs sur les 7 derniers jours */
  activeThisWeek: number;
  /** exercices terminés sur 7 jours */
  completed: number;
  /** réussite moyenne (arrondie) sur 7 jours, null sans exercice noté */
  avgScore: number | null;
};

/** Chiffres de la semaine des élèves liés actifs. PUR. */
export function weekStats(
  students: Pick<TutorLinkedStudent, 'status' | 'student_id' | 'last_activity_at'>[],
  attempts: RecentAttempt[],
  nowMs: number,
): WeekStats {
  const active = students.filter((s) => s.status === 'active');
  const ids = new Set(active.map((s) => s.student_id));
  const since = nowMs - INACTIVE_DAYS * DAY_MS;
  const week = attempts.filter(
    (a) => ids.has(a.student_id) && new Date(a.completed_at).getTime() >= since,
  );
  const workedIds = new Set(week.map((a) => a.student_id));
  for (const s of active) {
    if (s.last_activity_at && new Date(s.last_activity_at).getTime() >= since)
      workedIds.add(s.student_id);
  }
  const scored = week.map((a) => a.score).filter((n): n is number => typeof n === 'number');
  return {
    students: active.length,
    activeThisWeek: workedIds.size,
    completed: week.length,
    avgScore: scored.length
      ? Math.round(scored.reduce((sum, n) => sum + n, 0) / scored.length)
      : null,
  };
}

export type ActivityRow = RecentAttempt & { name: string };

/** Derniers exercices terminés, avec le nom de l'élève (élèves liés actifs seulement). PUR. */
export function recentActivityRows(
  attempts: RecentAttempt[],
  students: Pick<TutorLinkedStudent, 'status' | 'student_id' | 'display_name'>[],
  limit = 5,
): ActivityRow[] {
  const names = new Map(
    students.filter((s) => s.status === 'active').map((s) => [s.student_id, s.display_name]),
  );
  return [...attempts]
    .filter((a) => names.has(a.student_id))
    .sort((a, b) => b.completed_at.localeCompare(a.completed_at))
    .slice(0, limit)
    .map((a) => ({ ...a, name: names.get(a.student_id) ?? 'Élève' }));
}

/** « il y a 5 min », « il y a 3 h », « hier », « il y a 4 j ». PUR. */
export function agoLabel(iso: string, nowMs: number): string {
  const min = Math.max(0, Math.floor((nowMs - new Date(iso).getTime()) / 60_000));
  if (min < 1) return 'à l’instant';
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = daysSince(iso, nowMs) ?? Math.floor(h / 24);
  return d <= 1 ? 'hier' : `il y a ${d} j`;
}

/**
 * Exercices TERMINÉS par mes élèves liés sur les 7 derniers jours (RLS `attempts` : prof lié). Les ids partent
 * par lots de 100 (un `in()` de centaines d'UUID dépasse la longueur d'URL de PostgREST).
 */
export function useTutorRecentAttempts(tutorId: string, studentIds: string[]) {
  const key = [...studentIds].sort().join(',');
  return useQuery({
    queryKey: ['tutor-recent-attempts', tutorId, key],
    enabled: !!tutorId && studentIds.length > 0,
    staleTime: 60_000,
    queryFn: async (): Promise<RecentAttempt[]> => {
      const since = new Date(Date.now() - INACTIVE_DAYS * DAY_MS).toISOString();
      const chunks: string[][] = [];
      for (let i = 0; i < studentIds.length; i += 100) chunks.push(studentIds.slice(i, i + 100));
      const results = await Promise.all(
        chunks.map((ids) =>
          db
            .from('attempts')
            .select('id, student_id, score, completed_at, exercises(title)')
            .in('student_id', ids)
            .eq('status', 'completed')
            .gte('completed_at', since)
            .order('completed_at', { ascending: false })
            .limit(200),
        ),
      );
      const rows: RecentAttempt[] = [];
      for (const { data, error } of results) {
        if (error) throw new Error(error.message);
        for (const a of (data ?? []) as Record<string, unknown>[]) {
          if (typeof a.student_id !== 'string' || typeof a.completed_at !== 'string') continue;
          rows.push({
            id: String(a.id),
            student_id: a.student_id,
            exercise_title: (a.exercises as { title?: string } | null)?.title ?? 'Warmup',
            score: typeof a.score === 'number' ? a.score : null,
            completed_at: a.completed_at,
          });
        }
      }
      return rows;
    },
  });
}
